import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  Box,
} from "@material-ui/core";
import {
  Close,
  Person,
  Work,
  Business,
  LocationOn,
  Link,
  Info,
  Notes,
  Tune,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { contactsTypesActions } from "../../../../../store/contactTypes/contactTypesActions";

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: "6px",
    maxWidth: "900px", // Increased width
    width: "100%",
    overflow: "hidden", // Prevent content overflow
    maxHeight: "90vh",
  },
  dialogContent: {
    padding: "0",
    overflow: "auto",
    maxHeight: "calc(90vh - 70px)",
  },
  header: {
    fontSize: "22px",
    color: "#fff",
    padding: "15px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#3699FF",
    margin: 0,
    position: "relative",
    minHeight: "70px",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    lineHeight: 1.2,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "22px",
    color: "#fff",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 600,
    margin: 0,
    flex: 1,
    paddingRight: "40px",
    lineHeight: 1.2,
  },
  closeButton: {
    position: "absolute",
    top: "15px",
    right: "15px",
    color: "white",
    padding: "8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  content: {
    padding: "24px",
    background: "#f8fafc",
  },
  section: {
    marginBottom: "24px",
    background: "#fff",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e2e8f0",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "12px",
    borderBottom: "1px solid #e2e8f0",
  },
  sectionIcon: {
    marginRight: "10px",
    color: "#3699FF",
    fontSize: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#2d3748",
    margin: 0,
  },
  fieldRow: {
    marginBottom: "16px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  fieldLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  fieldValue: {
    fontSize: "14px",
    color: "#2d3748",
    fontWeight: 500,
    lineHeight: 1.5,
    padding: "8px 12px",
    background: "#f7fafc",
    borderRadius: "4px",
    border: "1px solid #e2e8f0",
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    wordBreak: "break-word",
  },
  emptyValue: {
    color: "#a0aec0",
    fontStyle: "italic",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#666",
  },
  notesSection: {
    marginBottom: "16px",
    padding: "12px",
    background: "#f0f9ff",
    borderRadius: "6px",
    border: "1px solid #bee3f8",
  },
  notesTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#3699FF",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  notesContent: {
    fontSize: "14px",
    color: "#2d3748",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  contactedBadge: {
    background: "#c6f6d5",
    color: "#22543d",
  },
  notContactedBadge: {
    background: "#fed7d7",
    color: "#742a2a",
  },
  linkValue: {
    color: "#3699FF",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "16px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  customFieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "12px",
  },
}));

const ProspectOverviewModal = ({ open, setOpen, prospectData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [prospectDetails, setProspectDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setProspectDetails(null);
    setError(null);
  };

  // Fetch prospect details from API
  const fetchProspectDetails = async prospectId => {
    if (!prospectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await dispatch(
        contactsTypesActions.onGetProspectById(prospectId)
      );
      if (response?.success) {
        setProspectDetails(response?.data);
      }

      setIsLoading(false);
    } catch (err) {
      console.error("Error fetching prospect details:", err);
      setError("Failed to fetch prospect details");
      toast.error("Failed to load prospect information");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && prospectData?.id) {
      fetchProspectDetails(prospectData.id);
    }
  }, [open, prospectData]);

  const formatDate = dateString => {
    if (!dateString) return "Not provided";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const renderField = (label, value, isLink = false, isStatus = false) => {
    const displayValue = value || "Not provided";

    return (
      <div className={classes.fieldRow}>
        <div className={classes.fieldLabel}>{label}</div>
        <div
          className={`${classes.fieldValue} ${
            !value ? classes.emptyValue : ""
          }`}>
          {isStatus ? (
            <span
              className={`${classes.statusBadge} ${
                value ? classes.contactedBadge : classes.notContactedBadge
              }`}>
              {value ? "Contacted" : "Not Contacted"}
            </span>
          ) : isLink && value ? (
            <a
              href={value.startsWith("http") ? value : `https://${value}`}
              target='_blank'
              rel='noopener noreferrer'
              className={classes.linkValue}>
              {displayValue}
            </a>
          ) : (
            displayValue
          )}
        </div>
      </div>
    );
  };

  const renderNotesSection = (title, content, icon) => {
    if (!content) return null;

    return (
      <div className={classes.notesSection}>
        <div className={classes.notesTitle}>
          {icon}
          {title}
        </div>
        <div className={classes.notesContent}>{content}</div>
      </div>
    );
  };

  const renderCustomField = (fieldNumber, value) => {
    if (!value) return null;

    return (
      <div className={classes.fieldRow}>
        <div className={classes.fieldLabel}>Custom Field {fieldNumber}</div>
        <div className={classes.fieldValue}>{value}</div>
      </div>
    );
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      classes={{
        paper: classes.dialogPaper,
      }}
      maxWidth={false}>
      <DialogTitle className={classes.header} disableTypography>
        <Typography variant='h2' className={classes.headerTitle}>
          Prospect Overview
        </Typography>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          size='small'>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.content}>
          {isLoading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={40} />
            </div>
          ) : error ? (
            <div className={classes.errorContainer}>
              <Typography variant='h6' color='error'>
                {error}
              </Typography>
              <Typography variant='body2' style={{ marginTop: "10px" }}>
                Please try again later.
              </Typography>
            </div>
          ) : prospectDetails ? (
            <>
              {/* Personal Information Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Person className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Personal Information
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField(
                      "Full Name",
                      `${prospectDetails.firstName || ""} ${
                        prospectDetails.lastName || ""
                      }`.trim()
                    )}
                    {renderField("First Name", prospectDetails.firstName)}
                    {renderField("Last Name", prospectDetails.lastName)}
                    {renderField("Nickname", prospectDetails.nickName)}
                    {/* {renderField(
                      "Date of Birth",
                      formatDate(prospectDetails.dob)
                    )}
                    {renderField("Gender", prospectDetails.gender)} */}
                  </div>
                  <div>
                    {renderField("Email Address", prospectDetails.email)}
                    {renderField("Contact Number", prospectDetails.contactNo)}
                    {renderField(
                      "Contact Status",
                      prospectDetails.isContacted,
                      false,
                      true
                    )}
                  </div>
                </div>
              </Paper>

              {/* Job Details Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Work className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Job Details
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField("Job Title", prospectDetails.title)}
                    {renderField("Occupation", prospectDetails.occupation)}
                  </div>
                  <div>
                    {renderField("Start Month", prospectDetails.startMonth)}
                    {renderField("Start Year", prospectDetails.startYear)}
                  </div>
                </div>
              </Paper>

              {/* Account Details Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Business className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Account Details
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField("Account Type", "Prospect")}
                    {renderField("Company Name", prospectDetails.companyName)}
                    {renderField(
                      "Number of Employees",
                      prospectDetails.noOfEmployees
                    )}
                    {renderField(
                      "Company Natural Name",
                      prospectDetails.companyNaturalName
                    )}
                  </div>
                  <div>
                    {renderField(
                      "Company LinkedIn",
                      prospectDetails.companyLinkeding
                    )}
                    {renderField(
                      "LinkedIn Employees",
                      prospectDetails.companyLinkedinEmployees
                    )}
                    {renderField(
                      "Company Followers",
                      prospectDetails.companyFollowers
                    )}
                    {renderField(
                      "Company Locality",
                      prospectDetails.companyLocality
                    )}
                  </div>
                </div>
              </Paper>

              {/* Location Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <LocationOn className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Location
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField("Address", prospectDetails.address1)}
                    {renderField("City", prospectDetails.city)}
                    {renderField("State", prospectDetails.state)}
                    {renderField("Time Zone", prospectDetails.timezone)}
                  </div>
                  <div>
                    {renderField("ZIP Code", prospectDetails.zipCode)}
                    {renderField("Country", prospectDetails.country)}
                    {renderField("Region", prospectDetails.region)}
                  </div>
                </div>
              </Paper>

              {/* Social Links Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Link className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Social Links
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField("Website", prospectDetails.website, true)}
                    {renderField("LinkedIn", prospectDetails.linkedin, true)}
                    {renderField("Twitter", prospectDetails.twitter, true)}
                  </div>
                  <div>
                    {renderField("Facebook", prospectDetails.facebook, true)}
                    {renderField("Instagram", prospectDetails.instagram, true)}
                    {renderField("Other", prospectDetails.other)}
                  </div>
                </div>
              </Paper>

              {/* Other Information Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Info className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Other Information
                  </Typography>
                </div>
                <div className={classes.twoColumnGrid}>
                  <div>
                    {renderField("Source", prospectDetails.source)}
                    {renderField("Lead Score", prospectDetails.leadScore)}
                  </div>
                  <div>
                    {renderField(
                      "On Market",
                      formatDate(prospectDetails.onMarket)
                    )}
                    {renderField("Campaign Name", prospectDetails.campaign)}
                  </div>
                </div>
              </Paper>

              {/* Notes Section */}
              {(prospectDetails.personalNotes ||
                prospectDetails.accountNotes ||
                prospectDetails.meetingNotes ||
                prospectDetails.internalNotes) && (
                <Paper className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <Notes className={classes.sectionIcon} />
                    <Typography className={classes.sectionTitle}>
                      Notes
                    </Typography>
                  </div>
                  {renderNotesSection(
                    "Personal Notes",
                    prospectDetails.personalNotes,
                    <Person fontSize='small' />
                  )}
                  {renderNotesSection(
                    "Account Notes",
                    prospectDetails.accountNotes,
                    <Business fontSize='small' />
                  )}
                  {renderNotesSection(
                    "Meeting Notes",
                    prospectDetails.meetingNotes,
                    <Work fontSize='small' />
                  )}
                  {renderNotesSection(
                    "Internal Notes",
                    prospectDetails.internalNotes,
                    <Info fontSize='small' />
                  )}
                </Paper>
              )}

              {/* Custom Fields Section */}
              <Paper className={classes.section}>
                <div className={classes.sectionHeader}>
                  <Tune className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    Custom Fields
                  </Typography>
                </div>
                <div className={classes.customFieldGrid}>
                  {renderCustomField(
                    1,
                    prospectDetails.customField1 || prospectDetails.cusomField1
                  )}
                  {renderCustomField(
                    2,
                    prospectDetails.customField2 || prospectDetails.cusomField2
                  )}
                  {renderCustomField(
                    3,
                    prospectDetails.customField3 || prospectDetails.cusomField3
                  )}
                  {renderCustomField(
                    4,
                    prospectDetails.customField4 || prospectDetails.cusomField4
                  )}
                  {renderCustomField(
                    5,
                    prospectDetails.customField5 || prospectDetails.cusomField5
                  )}
                  {renderCustomField(
                    6,
                    prospectDetails.customField6 || prospectDetails.cusomField6
                  )}
                </div>
              </Paper>
            </>
          ) : (
            <div className={classes.errorContainer}>
              <Typography variant='body2'>
                No prospect data available.
              </Typography>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProspectOverviewModal;
